import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartColumn } from '@fortawesome/free-solid-svg-icons';
import {
    ResponsiveContainer, ComposedChart, XAxis, YAxis, Tooltip, Legend, Bar, Line, CartesianGrid
} from 'recharts';
import api from '../../utils/api';
import '../../styles/App.css';
import '../../styles/usageReport.css';
import { DataTable, isoDay, fmtDur, fmtDate } from '../../components/usage/usageShared';
import CallBugsPanel from '../../components/usage/CallBugsPanel';
import FeatureUsagePanel from '../../components/usage/FeatureUsagePanel';

/**
 * Admin usage report: chat and call adoption.
 *
 * Metadata only. The server never returns message text; this page shows who
 * messaged or called whom, and how often. ADMIN-only on both ends.
 */

const PRESETS = [
    { key: '7', label: 'Last 7 days', days: 7 },
    { key: '30', label: 'Last 30 days', days: 30 },
    { key: '90', label: 'Last 90 days', days: 90 }
];

const TABS = [
    { key: 'users', label: 'By employee' },
    { key: 'direct', label: '1:1 chats' },
    { key: 'groups', label: 'Groups' },
    { key: 'calls', label: 'Calls' },
    { key: 'features', label: 'Features' },
    { key: 'bugs', label: 'Call bugs' }
];

const Kpi = ({ label, value, sub }) => (
    <div className="ur-kpi">
        <div className="ur-kpi-label">{label}</div>
        <div className="ur-kpi-value">{value}</div>
        {sub && <div className="ur-kpi-sub">{sub}</div>}
    </div>
);

const Meter = ({ label, pct, count, total, soft }) => (
    <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
            <span>{label}</span>
            <strong>{pct}%</strong>
        </div>
        <div className={`ur-meter ${soft ? 'is-soft' : ''}`}><span style={{ width: `${Math.min(pct, 100)}%` }} /></div>
        <div className="ur-kpi-sub">{count} of {total} employees</div>
    </div>
);

const UsageReport = () => {
    const [preset, setPreset] = useState('30');
    const [from, setFrom] = useState(isoDay(new Date(Date.now() - 30 * 86400000)));
    const [to, setTo] = useState(isoDay(new Date()));
    const [tab, setTab] = useState('users');
    const [summary, setSummary] = useState(null);
    const [data, setData] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const applyPreset = (p) => {
        setPreset(p.key);
        setFrom(isoDay(new Date(Date.now() - p.days * 86400000)));
        setTo(isoDay(new Date()));
    };

    // Typing a date passes through half-finished values (empty, year 0002, from after to).
    // Only a complete, sane range is sent; anything else clears the stale numbers.
    const rangeError = (() => {
        if (!from || !to) return 'Pick both a start and an end date.';
        if (from > to) return 'The start date must be on or before the end date.';
        if ((new Date(to) - new Date(from)) / 86400000 > 366) return 'Date range cannot exceed 366 days.';
        return '';
    })();

    useEffect(() => {
        if (rangeError) {
            setSummary(null);
            setData({});
            setError(rangeError);
            setLoading(false);
            return undefined;
        }
        let live = true;   // ignore responses from an older range
        setLoading(true);
        setError('');
        const params = { from, to };
        Promise.all([
            api.get('/admin-usage/summary', { params }),
            api.get('/admin-usage/users', { params }),
            api.get('/admin-usage/direct', { params }),
            api.get('/admin-usage/groups', { params }),
            api.get('/admin-usage/calls', { params })
        ]).then(([s, u, d, g, c]) => {
            if (!live) return;
            setSummary(s.data);
            setData({ users: u.data.users, direct: d.data.pairs, groups: g.data.groups, calls: c.data.pairs, workingDays: u.data.workingDays });
        }).catch(e => {
            if (!live) return;
            setSummary(null);
            setData({});
            setError(e.response?.data?.message || 'Could not load the usage report.');
        }).finally(() => { if (live) setLoading(false); });
        return () => { live = false; };
    }, [from, to, rangeError]);

    const tables = {
        users: {
            csv: 'usage-by-employee',
            empty: 'No employees found.',
            search: [r => r.name, r => r.department],
            cols: [
                { key: 'name', label: 'Employee', render: r => <><strong>{r.name}</strong> <span className="ur-pill">{r.department}</span></> },
                { key: 'direct', label: '1:1 msgs', num: true },
                { key: 'group', label: 'Group msgs', num: true },
                { key: 'callsStarted', label: 'Calls made', num: true },
                { key: 'callsJoined', label: 'Calls joined', num: true },
                { key: 'talkSeconds', label: 'Talk time', num: true, render: r => fmtDur(r.talkSeconds) },
                { key: 'activeDays', label: `Active days (of ${data.workingDays || '-'})`, num: true, render: r => `${r.activeDays} (${r.activeDaysPct}%)` },
                { key: 'logins', label: 'Logins web / mobile', num: true, value: r => r.logins.web + r.logins.mobile, render: r => `${r.logins.web} / ${r.logins.mobile}` },
                { key: 'lastLogin', label: 'Last login', render: r => fmtDate(r.lastLogin) }
            ]
        },
        direct: {
            csv: 'usage-direct-chats',
            empty: 'No 1:1 messages in this period.',
            search: [r => r.from?.name, r => r.to?.name],
            cols: [
                { key: 'from', label: 'From', value: r => r.from?.name, render: r => r.from?.name || 'Unknown' },
                { key: 'messages', label: 'Messages', num: true },
                { key: 'to', label: 'To', value: r => r.to?.name, render: r => r.to?.name || 'Unknown' },
                { key: 'last', label: 'Last message', render: r => fmtDate(r.last) }
            ]
        },
        groups: {
            csv: 'usage-groups',
            empty: 'No group messages in this period.',
            search: [r => r.name, r => r.topSenders.map(s => s.name).join(' ')],
            cols: [
                { key: 'name', label: 'Group', render: r => <><strong>{r.name}</strong> <span className="ur-pill">{r.groupType}</span></> },
                { key: 'messages', label: 'Messages', num: true },
                { key: 'members', label: 'Members', num: true },
                { key: 'activeSenders', label: 'Active senders', num: true },
                { key: 'topSenders', label: 'Top senders', value: r => r.topSenders.map(s => `${s.name} (${s.count})`).join(', '), render: r => r.topSenders.map(s => `${s.name} (${s.count})`).join(', ') },
                { key: 'last', label: 'Last message', render: r => fmtDate(r.last) }
            ]
        },
        calls: {
            csv: 'usage-calls',
            empty: 'No calls in this period.',
            search: [r => r.from?.name, r => r.to?.name],
            cols: [
                { key: 'from', label: 'Caller', value: r => r.from?.name, render: r => r.from?.name || 'Unknown' },
                { key: 'calls', label: 'Calls', num: true },
                { key: 'to', label: 'Callee', value: r => r.to?.name, render: r => r.to?.name || 'Unknown' },
                { key: 'answered', label: 'Answered', num: true },
                { key: 'missed', label: 'Missed', num: true },
                { key: 'declined', label: 'Declined', num: true },
                { key: 'video', label: 'Video', num: true },
                { key: 'seconds', label: 'Duration', num: true, render: r => fmtDur(r.seconds) },
                { key: 'last', label: 'Last call', render: r => fmtDate(r.last) }
            ]
        }
    };
    const t = tables[tab];
    const ad = summary?.adoption;

    return (
        <div className="attendance-container fade-in" style={{ paddingBottom: 40 }}>
            <h1 className="page-title">
                <FontAwesomeIcon icon={faChartColumn} style={{ marginRight: 10 }} />
                Usage Report
            </h1>
            <p className="text-muted" style={{ marginTop: -6, marginBottom: 16 }}>
                How much chat and calling the team is using. Shows who messaged or called whom and how often, never what was said.
            </p>

            <div className="ur-toolbar">
                {PRESETS.map(p => (
                    <button key={p.key} type="button" className={`ur-chip ${preset === p.key ? 'is-on' : ''}`} onClick={() => applyPreset(p)}>
                        {p.label}
                    </button>
                ))}
                <input type="date" value={from} max={to} onChange={e => { setPreset(''); setFrom(e.target.value); }} />
                <span className="text-muted">to</span>
                <input type="date" value={to} min={from} onChange={e => { setPreset(''); setTo(e.target.value); }} />
            </div>

            {error && <div className="ur-card" style={{ color: 'var(--theme-danger-ink)' }}>{error}</div>}
            {loading && !summary && <div className="ur-empty">Loading...</div>}

            {summary && (
                <>
                    <div className="ur-kpis" style={{ opacity: loading ? 0.6 : 1 }}>
                        <Kpi label="1:1 messages" value={summary.messages.direct.toLocaleString()} />
                        <Kpi label="Group messages" value={summary.messages.group.toLocaleString()} />
                        <Kpi label="Calls" value={summary.calls.total.toLocaleString()}
                            sub={`${summary.calls.audio} audio, ${summary.calls.video} video`} />
                        <Kpi label="Call time" value={fmtDur(summary.calls.seconds)}
                            sub={`${summary.calls.byReason.missed || 0} missed, ${summary.calls.byReason.failed || 0} failed`} />
                    </div>

                    <div className="ur-card">
                        <h3>Adoption</h3>
                        <div className="ur-hint">
                            "Regular" means active on at least {summary.regularThresholdDays} of the {summary.workingDays} working
                            days in this period. Chat and calls is the number to push up; any activity also counts logins.
                        </div>
                        <div className="ur-adopt">
                            <Meter label="Using chat or calls" pct={ad.chatCall.activePct} count={ad.chatCall.activeUsers} total={summary.employees} soft />
                            <Meter label="Regular on chat or calls" pct={ad.chatCall.regularPct} count={ad.chatCall.regularUsers} total={summary.employees} />
                            <Meter label="Any activity (incl. logins)" pct={ad.anyActivity.activePct} count={ad.anyActivity.activeUsers} total={summary.employees} soft />
                            <Meter label="Regular, any activity" pct={ad.anyActivity.regularPct} count={ad.anyActivity.regularUsers} total={summary.employees} />
                        </div>
                        <div className="ur-kpi-sub" style={{ marginTop: 12 }}>
                            Logins: {ad.logins.length
                                ? ad.logins.map(l => `${l.platform} ${l.logins} (${l.users} people)`).join(' · ')
                                : 'none recorded yet. Logins are tracked from this release onward.'}
                        </div>
                    </div>

                    <div className="ur-card">
                        <h3>Daily activity</h3>
                        <div style={{ width: '100%', height: 260 }}>
                            <ResponsiveContainer>
                                <ComposedChart data={summary.daily}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="var(--theme-border)" />
                                    <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={d => d.slice(5)} />
                                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                                    <Tooltip />
                                    <Legend />
                                    <Bar dataKey="messages" name="Messages" fill="var(--theme-blue-ink, #4c8bf5)" radius={[3, 3, 0, 0]} />
                                    <Line type="monotone" dataKey="calls" name="Calls" stroke="var(--theme-success-ink, #2e9e6b)" strokeWidth={2} dot={false} />
                                </ComposedChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    <div className="ur-card" style={{ opacity: loading ? 0.6 : 1, transition: 'opacity .15s' }}>
                        <div className="ur-tabs">
                            {TABS.map(x => (
                                <button key={x.key} type="button" className={`ur-chip ${tab === x.key ? 'is-on' : ''}`} onClick={() => setTab(x.key)}>
                                    {x.label}
                                </button>
                            ))}
                        </div>
                        {tab === 'features' && <FeatureUsagePanel from={from} to={to} />}
                        {tab === 'bugs' && <CallBugsPanel from={from} to={to} />}
                        {t && (
                            <DataTable
                                key={tab}
                                rows={data[tab] || []}
                                cols={t.cols}
                                searchKeys={t.search}
                                csvName={t.csv}
                                empty={t.empty}
                            />
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default UsageReport;
